import { ChevronRight } from 'lucide-react'
import { getRequirementSection } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import type { GroupedNode } from '@/hooks/useGroupedRequirements'
import type { BlockMeta } from '@/hooks/useBlockReorder'
import { useInlineEditor } from '@/hooks/useInlineEditor'
import { useAutoFocus } from '@/hooks/useAutoFocus'
import { useNotesSlashCommand } from '@/hooks/useNotesSlashCommand'
import { NotesBlockControls } from '../NotesBlockControls'
import { HoverInsertZone } from './HoverInsertZone'
import { PropertiesStrip } from './PropertiesStrip'
import { EpicSectionSelector } from './EpicSectionSelector'
import { RowDeleteButton } from '../RowDeleteButton'
import { DropIndicator } from '../DropIndicator'
import type { NotesSharedProps } from './notesShared'
import { NotesNode } from './NotesNode'

export function NotesEpic({ node, ...shared }: NotesSharedProps & { node: GroupedNode }) {
  const r = node.requirement
  const key = `e:${r.id}`
  const level = useRequirementsViewStore(s => s.notesLevel)
  const manuallyCollapsed = useRequirementsViewStore(s => s.notesCollapsedKeys.has(key))
  const toggleCollapse = useRequirementsViewStore(s => s.toggleNotesCollapse)
  const collapsed = level !== 'full' ? !manuallyCollapsed : manuallyCollapsed

  const handlers = useInlineEditor({
    value: r.name,
    onCommit: (v) => shared.onUpdate(r.id, { name: v }),
    singleLine: true,
  })
  const slash = useNotesSlashCommand({
    value: r.name,
    onPick: async (type) => {
      if (type === 'epic') return
      await shared.insertAfter(r, type, shared.personaSessionId)
    },
  })
  const autoFocusIfNew = useAutoFocus(r.id)

  const meta: BlockMeta = { type: 'epic', id: r.id, parentId: r.parentId, sortOrder: r.sortOrder }
  const dragProps = shared.dnd.makeDraggableProps(meta)
  const dropProps = shared.dnd.makeDropZoneProps(meta)
  const kbProps = shared.dnd.makeKeyboardReorderProps(meta)

  const handleInput: React.FormEventHandler<HTMLElement> = (e) => {
    if (slash.onInput(e)) return
    handlers.onInput(e)
  }
  const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (slash.isOpen) return
    if (e.key === 'Enter' && !e.shiftKey) {
      handlers.onKeyDown(e)
      void shared.insertAfter(r, 'epic', shared.personaSessionId)
      return
    }
    handlers.onKeyDown(e)
  }
  const handleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    if (slash.onBlur(e)) return
    handlers.onBlur(e)
  }

  const kindLabel = r.type === 'nfr-category' ? 'NFR' : 'Epic'

  return (
    <section
      className="group relative"
      style={{
        marginTop: 40,
        opacity: shared.dnd.isDragging(meta) ? 0.4 : 1,
      }}
      data-req-id={r.id}
      {...dropProps}
    >
      <NotesBlockControls
        draggableProps={dragProps}
        keyboardReorderProps={kbProps}
        onInsert={(t) => shared.insertAfter(r, t, shared.personaSessionId)}
      />
      {shared.dnd.isDragTargetAbove(meta) && <DropIndicator />}

      {/* Eyebrow: kind + section selector. Subtle, hover-revealed for low chrome. */}
      <div
        className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity"
        style={{
          fontSize: 11,
          color: 'var(--color-fg-muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontWeight: 600,
          marginBottom: 4,
        }}
      >
        <span style={{ color: 'var(--color-solution-700)' }}>{kindLabel}</span>
        <span aria-hidden style={{ opacity: 0.4 }}>·</span>
        <EpicSectionSelector
          value={getRequirementSection(r)}
          onChange={(section) => {
            void shared.onUpdate(r.id, { metadata: { ...r.metadata, section } })
          }}
        />
        <div className="flex-1" />
        <div className="opacity-0 group-hover:opacity-100 transition-opacity normal-case tracking-normal">
          <RowDeleteButton onDelete={() => { void shared.onDelete(r.id) }} label="epic" />
        </div>
      </div>

      {/* Title row */}
      <div className="flex items-baseline gap-2">
        <button
          onClick={() => toggleCollapse(key)}
          className="flex-shrink-0 rounded p-0.5 hover:bg-warm-100 -ml-6"
          style={{ color: 'var(--color-fg-muted)' }}
          aria-label={collapsed ? 'Expand epic' : 'Collapse epic'}
        >
          <ChevronRight size={14} style={{ transform: collapsed ? 'rotate(0)' : 'rotate(90deg)', transition: 'transform 120ms' }} />
        </button>
        <h2
          ref={(el) => {
            ;(handlers.ref as { current: HTMLElement | null }).current = el
            autoFocusIfNew(el)
          }}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Epic name"
          aria-multiline="false"
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlers.onPaste}
          onBlur={handleBlur}
          spellCheck={false}
          data-placeholder="Untitled epic"
          className="m-0 outline-none rounded flex-1 min-w-0"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1.25,
            letterSpacing: '-0.012em',
            color: 'var(--color-fg)',
          }}
        >{r.name}</h2>
      </div>

      {/* Properties strip — single muted line, click to expand */}
      <div className="mt-1.5">
        <PropertiesStrip requirement={r} themes={shared.themes} />
      </div>

      {slash.menu}
      {!collapsed && (
        <div className="mt-3">
          {node.children.map(c => <NotesNode key={c.key} node={c} {...shared} />)}
          <div className="mt-2">
            <HoverInsertZone
              defaultType="story"
              parents={{ story: r.id, epic: r.parentId ?? undefined }}
              personaSessionId={shared.personaSessionId}
              onCreate={shared.onCreate}
              emptyHint={node.children.length === 0 ? 'Add story' : undefined}
            />
          </div>
        </div>
      )}
      {shared.dnd.isDragTargetBelow(meta) && <DropIndicator />}
    </section>
  )
}
